import type {
  AllBlocks,
  Asset,
  Link,
  StoryblokStory,
  StoryLink,
  UrlOrStoryLink,
} from '@storyblok-types';
import { pagesSlug } from '../../routes/routing';

// TODO: We might want to just translate this into object-position percentages
// but then we depend on inline styles on images which is not ideal.
export const calculateFocalPoint = (asset: Asset): 'left' | 'center' | 'right' => {
  if (!asset.focus) {
    return 'center';
  }
  const pathSegments = asset.filename.split('/');
  for (const pathSegment of pathSegments) {
    const widthAndHeight = /([0-9]+)x([0-9]+)/.exec(pathSegment);
    if (widthAndHeight) {
      const width = parseInt(widthAndHeight[1], 10);
      const focalPoint = /([0-9]+)x([0-9]+):([0-9]+)x([0-9]+)/.exec(asset.focus);
      if (focalPoint) {
        const left = parseInt(focalPoint[1], 10);
        if (width / 3 > left) {
          return 'left';
        } else if ((width / 3) * 2 < left) {
          return 'right';
        } else {
          return 'center';
        }
      }
    }
  }
  return 'center';
};

type StoryBlokUrl = {
  isExternal: boolean;
  href: string;
};
const withAnchor = (storyBlokUrl: StoryBlokUrl, linkBlok: Link): StoryBlokUrl => {
  const anchor = 'anchor' in linkBlok ? linkBlok.anchor?.trim() : undefined;
  const formattedAnchor = anchor ? `#${anchor}` : '';

  return { ...storyBlokUrl, href: `${storyBlokUrl.href}${formattedAnchor}` };
};

export function getUrlByLinkType(
  linkBlok: Link | undefined,
  cached: boolean = true,
): StoryBlokUrl | undefined {
  if (isMultilinkStoryblokWithValidUrl(linkBlok)) {
    return withAnchor(
      {
        isExternal: false,
        href: linkBlok.computedUrl,
      },
      linkBlok,
    );
  }

  switch (linkBlok?.linktype) {
    case 'url': {
      return withAnchor(urlFromExternal(cached, linkBlok), linkBlok);
    }
    case 'story': {
      const storyLink = urlFromLinkPropStory(cached, linkBlok);
      if (storyLink) {
        return withAnchor(storyLink, linkBlok);
      }
      return storyLink;
    }
    case 'asset': {
      return withAnchor(
        {
          isExternal: false,
          href: cached ? (linkBlok.cached_url ?? linkBlok.url ?? '') : (linkBlok.url ?? ''),
        },
        linkBlok,
      );
    }
    default: {
      return {
        isExternal: false,
        href: '',
      };
    }
  }
}

export function getNameByLinkType(linkBlock: { link?: UrlOrStoryLink; text?: string }): string {
  const link = linkBlock.link;
  const layoutText = linkBlock.text?.trim() ? linkBlock.text : undefined;
  if (link?.linktype !== 'story' || !link.story) {
    return layoutText ?? '';
  }
  const translatedName = link.story.translated_name;
  const storyName = link.story.name;
  return layoutText ?? translatedName ?? storyName ?? '';
}

function urlFromLinkPropStory(cached: boolean, linkBlok: StoryLink): StoryBlokUrl | undefined {
  const url = cached
    ? (linkBlok.cached_url ?? linkBlok.story?.full_slug ?? '')
    : (linkBlok.story?.full_slug ?? '');
  if (url.trim() == '') {
    return undefined;
  }

  return {
    isExternal: false,
    href: normalize(url),
  };
}

function urlFromExternal(cached: boolean, linkBlok: { url?: string; cached_url?: string }) {
  const href = cached ? (linkBlok.cached_url ?? linkBlok.url ?? '') : (linkBlok.url ?? '');
  return {
    isExternal: !href.startsWith('#'),
    href,
  };
}

export function isComponentReference(obj: unknown): obj is AllBlocks {
  if (typeof obj !== 'object' || !obj) {
    return false;
  }
  return (
    'component' in obj &&
    typeof obj['component'] === 'string' &&
    '_uid' in obj &&
    typeof obj['_uid'] === 'string'
  );
}
export type LinkWithValidUrl = Link & { computedUrl: string };

export const isMultilinkStoryblokWithValidUrl = (obj: unknown): obj is LinkWithValidUrl => {
  if (!obj) {
    return false;
  }
  const link = obj as LinkWithValidUrl;
  return !!link.computedUrl && link.linktype === 'story';
};

export type StoryblokTypeMapping = { [K in AllBlocks as K['component']]: K };

export const isBlok =
  <Key extends keyof StoryblokTypeMapping>(...keys: Key[]) =>
  (value: unknown): value is StoryblokTypeMapping[Key] =>
    isComponentReference(value) && keys.includes(value.component as Key);

// Storyblok does not consistently return the slug with a leading slash
function normalize(str: string) {
  if (!str.startsWith('/')) {
    str = '/' + str;
  }

  return str.replace(new RegExp('^\\/' + pagesSlug), '');
}

export function preventLinkClicksWhenInEditorMode() {
  const currentUrl = new URL(window.location.href);
  if (currentUrl.searchParams.get('_storyblok')) {
    document.body.addEventListener(
      'click',
      (e) => {
        let target = e.target as HTMLElement | null;
        while (target) {
          if (target.tagName === 'A') {
            console.log('Prevented link click in editor mode', target.getAttribute('href'));
            e.preventDefault();
          }
          target = target.parentElement;
        }
      },
      { capture: true },
    );
  }
}

export const isBlock = (value: unknown): value is AllBlocks => {
  const block = value as AllBlocks;
  return !!block && !!block.component && !!block._uid;
};

export const isStory = (value: unknown): value is StoryblokStory<AllBlocks> => {
  const block = value as StoryblokStory<AllBlocks>;
  return !!block && !!block.id && !!block.content && isBlock(block.content);
};
